import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchAppData } from "./appDataSlice";
import { LocaleState, Country, Currency } from "./types/locale-types";
import { RootState } from "../../redux/store";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }
  return payload;
}

export const fetchUserCountry = createAsyncThunk<
  { country: string; countryCode: string },
  void,
  { rejectValue: string; state: RootState }
>("locale/fetchUserCountry", async (_, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;

  try {
    const url = new URL(`${API_BASE}/user-country`);
    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload: any = await handleResp(res);
    // Extract data logic
    return payload;
  } catch (err: any) {
    // Log error when network or parsing fails
    console.error("Fetch Countries Network/Parsing Error:", err.message);
    return rejectWithValue(err?.message ?? "Failed to fetch countries");
  }
});

const initialState: LocaleState = {
  country: { items: [], loading: false, error: null },
  currency: { items: [], loading: false, error: null },
  selectedCountry: {
    id: 3,
    name: "Others",
    symbol: "USA",
    default_currency_id: 3,
  },
  selectedCurrency: {
    id: 3,
    name: "US Dollar (USD)",
    symbol: "$",
    exchange_rate: 1.0,
  },
  userLocation: { data: null, loading: false, error: null, attempted: false },
};

const localeSlice = createSlice({
  name: "locale",
  initialState,
  reducers: {
    setSelectedCountry(state, action: PayloadAction<Country>) {
      state.selectedCountry = action.payload;
      if (action.payload.currency) {
        state.selectedCurrency = action.payload.currency;
      }
    },
    setSelectedCurrency(state, action: PayloadAction<Currency>) {
      state.selectedCurrency = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppData.pending, (state) => {
        state.country.loading = true;
        state.currency.loading = true;
      })
      .addCase(fetchAppData.fulfilled, (state, action) => {
        state.country.loading = false;
        state.currency.loading = false;

        // Extracting from your specific JSON structure: action.payload.data
        const appData = action.payload;
        if (appData) {
          state.country.items = appData.countries || [];
          state.currency.items = appData.currencies || [];
        }
      })
      .addCase(fetchAppData.rejected, (state, action) => {
        state.country.loading = false;
        state.currency.loading = false;
        state.country.error =
          action.error.message || "Failed to load locale data";
      })
      .addCase(fetchUserCountry.pending, (state) => {
        state.userLocation.loading = true;
        state.userLocation.error = null;
        state.userLocation.attempted = false;
      })
      .addCase(fetchUserCountry.fulfilled, (state, action) => {
        state.userLocation.loading = false;
        state.userLocation.data = action.payload;
        state.userLocation.attempted = true;
      })
      .addCase(fetchUserCountry.rejected, (state, action) => {
        state.userLocation.loading = false;
        state.userLocation.attempted = true;
        state.userLocation.error =
          (action.payload as string) || "Failed to get user country";
      });
  },
});

export const { setSelectedCountry, setSelectedCurrency } = localeSlice.actions;
export default localeSlice.reducer;
